import * as React from 'react';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const defaultColors = [
  'hsl(220, 20%, 65%)',
  'hsl(220, 20%, 42%)',
  'hsl(220, 20%, 35%)',
  'hsl(220, 20%, 25%)',
  'hsl(220, 20%, 55%)',
  'hsl(220, 20%, 48%)',
];

export default function ChartLoansByBranch({ branchData = { branches: [], total_loans: 0, total_amount: 0 } }) {
  const { branches, total_loans, total_amount } = branchData;

  // If no data, show empty state
  if (!branches || branches.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
      >
        <CardContent>
          <Typography component="h2" variant="subtitle2">
            Préstamos por Sucursal
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
            <Typography variant="body2" color="text.secondary">
              No hay datos de préstamos disponibles
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for pie chart
  const pieData = branches.map(branch => ({
    label: branch.label,
    value: branch.value,
    color: branch.color || defaultColors[branches.indexOf(branch) % defaultColors.length]
  }));

  const colors = pieData.map(item => item.color);

  // Format total amount for display
  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Préstamos por Sucursal
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={colors}
            margin={{
              left: 80,
              right: 80,
              top: 80,
              bottom: 80,
            }}
            series={[
              {
                data: pieData,
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { fade: 'global', highlight: 'item' },
              },
            ]}
            height={260}
            width={260}
            hideLegend
          >
            <PieCenterLabel 
              primaryText={total_loans.toString()} 
              secondaryText={`${formatAmount(total_amount)}`} 
            />
          </PieChart>
        </Box>
        {branches.map((branch, index) => (
          <Stack
            key={index}
            direction="row"
            sx={{ alignItems: 'center', gap: 2, pb: 2 }}
          >
            <BusinessIcon sx={{ color: branch.color || defaultColors[index % defaultColors.length] }} />
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {branch.label}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {branch.value} préstamos
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    ({branch.percentage}%)
                  </Typography>
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={`Préstamos en ${branch.label}`}
                value={branch.percentage}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: branch.color || defaultColors[index % defaultColors.length],
                  },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AttachMoneyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatAmount(branch.amount)}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

ChartLoansByBranch.propTypes = {
  branchData: PropTypes.shape({
    branches: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
      amount: PropTypes.number,
      percentage: PropTypes.number,
      color: PropTypes.string,
    })),
    total_loans: PropTypes.number,
    total_amount: PropTypes.number,
  }),
};
